import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { fetchEventSource } from "@microsoft/fetch-event-source";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { VoiceRecorder } from "@/components/VoiceRecorder";
import {
  ArrowLeft, Send, BarChart3, Target, MessageCircle,
  Plus, ThumbsUp, ThumbsDown, Loader2, Paperclip, X, Square, MoreHorizontal, History,
  FileText, Image as ImageIcon, TrendingUp, Sparkles, Landmark,
  Download, FileDown, Globe, ExternalLink, ChevronDown, ChevronUp,
  Volume2, Play, Pause, Lightbulb, Sailboat, ChevronRight
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { parseFollowup, stripFollowup } from "@/lib/followup";
import DossierPanel from "@/components/DossierPanel";
import RichMessage from "@/components/RichMessage";
import FollowUpSheet from "@/components/FollowUpSheet";
import FavoriteButton from "@/components/FavoriteButton";
import AiGeneratedBadge from "@/components/AiGeneratedBadge";
import { DeepThinkingPanel } from "@/components/DeepThinkingPanel";
import { CollegialPanel } from "@/components/CollegialPanel";
import { MessageSquareText } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AGENT_AVATARS = {
  routeur: "https://static.prod-images.emergentagent.com/jobs/0c949b6c-2246-4cdd-8394-1b138055d0de/images/8a32cad401844db2c660f0152ac9e85b5847bbde3ca554477ea01904f8465f9b.png",
  analyse: "https://static.prod-images.emergentagent.com/jobs/0c949b6c-2246-4cdd-8394-1b138055d0de/images/19a86bf97520c7466c87f96c95a7fb5682566a3bb16f679be5d814a1aaebd419.png",
  ia: "https://static.prod-images.emergentagent.com/jobs/0c949b6c-2246-4cdd-8394-1b138055d0de/images/5c3ff245316d2ec62c555a457b600242ef0de940248d2c53a85d86424d732f1c.png",
  strategie: "https://static.prod-images.emergentagent.com/jobs/0c949b6c-2246-4cdd-8394-1b138055d0de/images/a847752862b35df5e195ab26f0a225f02d9e2ee6159f7a7a1e53694a20a7cdab.png",
  nego: "https://static.prod-images.emergentagent.com/jobs/0c949b6c-2246-4cdd-8394-1b138055d0de/images/4889000b20dcadf5cae0644c4005446f0f2df56d3e42f67d7c61bd19baef64d8.png",
  banque: "https://static.prod-images.emergentagent.com/jobs/0c949b6c-2246-4cdd-8394-1b138055d0de/images/4348a57bda3ad71e0c15bfa2f66c4feb4891a609193bab30cbe74908226f41a9.png",
  travaux: "https://static.prod-images.emergentagent.com/jobs/0c949b6c-2246-4cdd-8394-1b138055d0de/images/27f670b872ef630033ea86eddb35f1a5cbbfba487ba70b64919bec800d6785d1.png",
  fiscalite: "https://static.prod-images.emergentagent.com/jobs/0c949b6c-2246-4cdd-8394-1b138055d0de/images/79686ac1613549b6d0093e4da9dec98ef8ee543744a38527a4d24646f507da62.png",
  locataire: "https://static.prod-images.emergentagent.com/jobs/0c949b6c-2246-4cdd-8394-1b138055d0de/images/cd83e63bc00b54b2729dfa7347ffac350c80bf94cfe84c6c2f1955c58acd9faa.png",
  marche: "https://static.prod-images.emergentagent.com/jobs/0c949b6c-2246-4cdd-8394-1b138055d0de/images/4eeb12fb01e2fa4178fa72384eafe29396c02d2fe9477f7d1174207db4a453ba.png",
  juridique: "https://static.prod-images.emergentagent.com/jobs/0c949b6c-2246-4cdd-8394-1b138055d0de/images/b9295ba9fe52e4da445b0eb8e245b1ee8f3c5ca69956a48976b72ab78714f30a.png",
  notaire: "https://static.prod-images.emergentagent.com/jobs/0c949b6c-2246-4cdd-8394-1b138055d0de/images/0e489a0250e58a57ee0224da1c1e4af8d80ee0b8af54036914406cf159d489f6.png",
};

const AGENT_INFO = {
  routeur: {
    name: "ODYS'GUIDE",
    desc: "Ton guide personnel. Je t'oriente et je cree ton plan d'action.",
    icon: Target,
    color: "#16233B",
    avatar: AGENT_AVATARS.routeur,
    greeting: "Salut ! Je suis ODYS'GUIDE, ton guide personnel. Dis-moi ce que tu cherches a faire et je t'oriente vers le bon expert. Tu peux aussi me demander un plan d'action personnalise pour ton projet immobilier !",
    templates: [
      "Cree-moi un plan d'action pour mon premier investissement",
      "Je veux investir avec 50 000€ d'apport, par ou commencer ?",
      "Quel agent peut m'aider pour negocier un bien ?",
      "Je debute, guide-moi etape par etape",
    ],
  },
  analyse: {
    name: "ODYS'ANALYSE",
    desc: "Expert en analyse de rentabilite immobiliere.",
    icon: BarChart3,
    color: "#FF8C42",
    avatar: AGENT_AVATARS.analyse,
    greeting: "Salut ! Je suis la pour t'aider a analyser la rentabilite d'un bien immobilier. Donne-moi les infos du bien que tu veux analyser (prix, loyer prevu, charges...).",
    templates: [
      "Analyse ce bien : 150 000€, loyer 700€/mois, charges 100€",
      "Calcule la rentabilite nette d'un studio a 80 000€",
      "Est-ce un bon investissement si je mets 20% d'apport ?",
      "Compare la renta entre un T2 et un T3 dans la meme ville",
    ],
  },
  ia: {
    name: "ODYS'ASSISTANT",
    desc: "Ton assistant IA 24/7 pour toutes tes questions immo.",
    icon: MessageCircle,
    color: "#10B981",
    avatar: AGENT_AVATARS.ia,
    greeting: "Salut ! Je suis ODYS'ASSISTANT, ton assistant immobilier. Pose-moi n'importe quelle question sur l'investissement immobilier.",
    templates: [
      "Explique-moi la difference entre LMNP et LMP",
      "C'est quoi le deficit foncier ?",
      "Comment fonctionne un pret in fine ?",
      "Quels sont les pieges a eviter en immo ?",
    ],
  },
  strategie: {
    name: "ODYS'STRATEGIE",
    desc: "Expert en strategie d'investissement immobilier.",
    icon: TrendingUp,
    color: "#6366F1",
    avatar: AGENT_AVATARS.strategie,
    greeting: "Salut ! Je suis ODYS'STRATEGIE. Je vais t'aider a definir ta strategie d'investissement. Dis-moi : quel est ton objectif principal ? (cashflow, patrimoine, les deux ?)",
    templates: [
      "Quelle strategie pour 100 000€ d'apport ?",
      "Je veux du cashflow immediat, que faire ?",
      "Comment construire un patrimoine sur 10 ans ?",
      "Vaut-il mieux un bien ou plusieurs petits ?",
    ],
  },
  nego: {
    name: "ODYS'NEGO",
    desc: "Expert en negociation immobiliere.",
    icon: Sparkles,
    color: "#EC4899",
    avatar: AGENT_AVATARS.nego,
    greeting: "Salut ! Je suis ODYS'NEGO, ton coach en negociation. Tu as un bien en vue ? Donne-moi le prix affiche et les infos du bien, et on prepare ta strategie de nego.",
    templates: [
      "Comment negocier un bien affiche a 200 000€ ?",
      "Le vendeur refuse de baisser, que faire ?",
      "Quels arguments utiliser pour negocier ?",
      "Le bien est en vente depuis 6 mois, combien demander ?",
    ],
  },
  banque: {
    name: "ODYS'BANQUE",
    desc: "Expert en dossier bancaire et credit immobilier.",
    icon: Landmark,
    color: "#0EA5E9",
    avatar: AGENT_AVATARS.banque,
    greeting: "Salut ! Je suis ODYS'BANQUE. Je vais t'aider a construire un dossier bancaire solide. Commenceons par ta situation : es-tu en CDI, independant, fonctionnaire ?",
    templates: [
      "Comment optimiser mon dossier bancaire ?",
      "Je gagne 3000€/mois, combien puis-je emprunter ?",
      "Taux fixe ou variable en 2024 ?",
      "Comment convaincre la banque pour un 2eme investissement ?",
    ],
  },
  travaux: {
    name: "ODYS'TRAVAUX",
    desc: "Expert en budgetisation de travaux de renovation.",
    icon: Target,
    color: "#D97706",
    avatar: AGENT_AVATARS.travaux,
    greeting: "Salut ! Je suis ODYS'TRAVAUX. Je vais t'aider a estimer le budget des travaux pour ton bien. Decris-moi le bien : surface, etat actuel, et ce que tu veux faire comme travaux.",
    templates: [
      "Estime les travaux pour un T3 de 60m² a rafraichir",
      "Budget renovation complete d'un appartement annees 70",
      "Combien coute une salle de bain neuve ?",
      "Quels travaux pour transformer un local en habitation ?",
    ],
  },
  fiscalite: {
    name: "ODYS'FISCALITE",
    desc: "Expert en optimisation fiscale immobiliere.",
    icon: BarChart3,
    color: "#7C3AED",
    avatar: AGENT_AVATARS.fiscalite,
    greeting: "Salut ! Je suis ODYS'FISCALITE. Je vais t'aider a choisir le meilleur regime fiscal pour ton investissement. Dis-moi : tu prevois de louer en nu ou en meuble ?",
    templates: [
      "LMNP reel ou micro-BIC, que choisir ?",
      "Comment payer moins d'impots sur mes loyers ?",
      "Explique-moi le deficit foncier",
      "SCI a l'IR ou a l'IS pour de la location ?",
    ],
  },
  locataire: {
    name: "ODYS'LOCATAIRE",
    desc: "Expert en selection de locataires et gestion locative.",
    icon: MessageCircle,
    color: "#059669",
    avatar: AGENT_AVATARS.locataire,
    greeting: "Salut ! Je suis ODYS'LOCATAIRE. Je t'aide sur la selection de locataires ET la gestion locative (copropriete, monopropriete, immeuble). Quelle est ta question ?",
    templates: [
      "Quels documents demander a un locataire ?",
      "Comment verifier la solvabilite d'un candidat ?",
      "Mon locataire ne paie plus, que faire ?",
      "Gestion en direct ou agence, que conseilles-tu ?",
    ],
  },
  marche: {
    name: "ODYS'MARCHE",
    desc: "Expert en analyse de marche et comparaison de villes.",
    icon: Target,
    color: "#14B8A6",
    avatar: AGENT_AVATARS.marche,
    greeting: "Salut ! Je suis ODYS'MARCHE. Je t'aide a analyser les marches immobiliers et comparer les villes pour investir. Quelle ville ou region t'interesse ?",
    templates: [
      "Quelles villes pour investir en 2024 ?",
      "Compare Lyon et Bordeaux pour un investissement",
      "Le marche va-t-il baisser cette annee ?",
      "Ou trouver du cashflow positif en France ?",
    ],
  },
  juridique: {
    name: "ODYS'JURIDIQUE",
    desc: "Expert en questions juridiques immobilieres.",
    icon: BarChart3,
    color: "#8B5CF6",
    avatar: AGENT_AVATARS.juridique,
    greeting: "Salut ! Je suis ODYS'JURIDIQUE. Je t'aide sur les questions juridiques : baux, contrats, copropriete, litiges... Quelle est ta question ?",
    templates: [
      "Que doit contenir un bail de location ?",
      "Mon locataire degrade le bien, quels recours ?",
      "Difference entre bail nu et bail meuble",
      "Puis-je refuser un locataire avec garant ?",
    ],
  },
  notaire: {
    name: "ODYS'NOTAIRE",
    desc: "Expert en demarches notariales.",
    icon: BarChart3,
    color: "#374151",
    avatar: AGENT_AVATARS.notaire,
    greeting: "Salut ! Je suis ODYS'NOTAIRE. Je t'accompagne dans tes demarches notariales : compromis, acte authentique, frais de notaire... Ou en es-tu dans ton projet ?",
    templates: [
      "Combien de frais de notaire pour 150 000€ ?",
      "Que verifier dans un compromis de vente ?",
      "Delai entre compromis et acte definitif ?",
      "Puis-je negocier les frais de notaire ?",
    ],
  },
};

function WebSourcesBadge({ sources, index }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="mt-2" data-testid={`web-search-badge-${index}`}>
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-1.5 text-[11px] text-green-600 font-medium hover:text-green-700 transition-colors"
        data-testid={`web-sources-toggle-${index}`}
      >
        <Globe className="w-3 h-3" />
        <span>Donnees web en temps reel</span>
        {sources.length > 0 && (
          <>
            <span className="text-green-400">·</span>
            <span className="text-green-500">{sources.length} source{sources.length > 1 ? "s" : ""}</span>
            {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          </>
        )}
      </button>
      {expanded && sources.length > 0 && (
        <div className="mt-1.5 pl-4 border-l-2 border-green-200 space-y-1">
          {sources.map((src, j) => (
            <a
              key={j}
              href={src.url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 text-[10px] text-slate-500 hover:text-[#16233B] transition-colors group"
              data-testid={`web-source-link-${index}-${j}`}
            >
              <ExternalLink className="w-2.5 h-2.5 shrink-0 group-hover:text-[#16233B]" />
              <span className="truncate">{src.title || src.url}</span>
            </a>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AgentChatPage() {
  const { agentType } = useParams();
  const [searchParams] = useSearchParams();
  const convParam = searchParams.get("conversation");
  const navigate = useNavigate();
  const { api, token } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [visitReportLoading, setVisitReportLoading] = useState(null); // message_id en cours de generation
  const [conversations, setConversations] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [attachedFiles, setAttachedFiles] = useState([]); // Changed to array
  const [uploading, setUploading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [feedbackGiven, setFeedbackGiven] = useState(null); // 'up' or 'down' or null
  // Auto = l'expert decide lui-meme s'il doit verifier sur le web (detecteur Haiku cote backend)
  const [isRecordingVoice, setIsRecordingVoice] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [showTemplates, setShowTemplates] = useState(true);
  const [followUpState, setFollowUpState] = useState({ open: false, questions: [], msgIndex: null });
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const abortRef = useRef(null);            // permet d'arreter une generation en cours
  const scrollWrapRef = useRef(null);      // conteneur de defilement des messages
  const [showScrollDown, setShowScrollDown] = useState(false);
  const [histQuery, setHistQuery] = useState("");
  const [showDisclaimer, setShowDisclaimer] = useState(false);

  // Echap coupe la generation en cours (desktop)
  useEffect(() => {
    if (!sending) return;
    const onKey = (e) => { if (e.key === "Escape") stopGeneration(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [sending]); // eslint-disable-line react-hooks/exhaustive-deps

  // Bouton "revenir en bas" : on ecoute directement le viewport de la zone de messages
  useEffect(() => {
    const vp = scrollWrapRef.current?.querySelector("[data-radix-scroll-area-viewport]");
    if (!vp) return;
    const onScroll = () => {
      setShowScrollDown(vp.scrollHeight - vp.scrollTop - vp.clientHeight > 320);
    };
    vp.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => vp.removeEventListener("scroll", onScroll);
  }, [agentType, conversationId]);
  const fileInputRef = useRef(null);

  const agent = AGENT_INFO[agentType];
  
  // Check if analysis is complete (has [ANALYSE_COMPLETE] in messages) - for analyse agent
  const hasCompleteAnalysis = agentType === "analyse" && messages.some(
    m => m.role === "assistant" && m.content?.includes("[ANALYSE_COMPLETE]")
  );
  
  // Export PDF is available for all agents when there are assistant messages
  const canExportPDF = conversationId && messages.some(m => m.role === "assistant");

  // Handle template click
  const handleTemplateClick = (template) => {
    setInput(template);
    setShowTemplates(false);
    inputRef.current?.focus();
  };

  // Submit feedback
  const submitFeedback = async (feedbackType) => {
    if (!conversationId || feedbackGiven) return;
    
    try {
      await axios.post(`${API}/feedback`, {
        conversation_id: conversationId,
        feedback_type: feedbackType,
        agent_type: agentType
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFeedbackGiven(feedbackType);
      toast.success(feedbackType === 'up' ? 'Merci pour ton retour positif !' : 'Merci, on va s\'ameliorer !');
    } catch (err) {
      console.error('Feedback error:', err);
    }
  };

  useEffect(() => {
    if (!agent) {
      navigate("/dashboard");
      return;
    }
    loadConversations();
    setMessages([{ role: "assistant", content: agent.greeting }]);
    setConversationId(null);
    setAttachedFiles([]); // Reset to empty array
    setFeedbackGiven(null);
    setShowTemplates(true); // Show templates for new conversation

    // Ouverture directe d'une conversation existante (Favoris / Historique)
    if (convParam) {
      setShowTemplates(false);
      loadConversation(convParam);
      return;
    }

    // Pickup d'un message en attente depuis Reunion d'equipage (followup verdict)
    try {
      const pending = sessionStorage.getItem("odys_pending_message");
      if (pending) {
        sessionStorage.removeItem("odys_pending_message");
        // Lance l'envoi apres un court delai pour que la page soit prete
        setTimeout(() => {
          handleSend(pending);
        }, 300);
      }
    } catch {}
  }, [agentType, convParam]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const loadConversations = async () => {
    try {
      const res = await api().get(`/chat/conversations?agent_type=${agentType}`);
      setConversations(res.data);
    } catch {}
  };

  const loadConversation = async (convId) => {
    try {
      const res = await api().get(`/chat/conversations/${convId}/messages`);
      setMessages(res.data.map((m) => ({
        role: m.role,
        content: m.content,
        file_url: m.file_url,
        file_name: m.file_name,
        collegial: m.collegial?.experts
          ? { ...m.collegial, activeIndex: -1, phase: "done" }
          : null,
        deep: m.deep_research?.plan
          ? {
              plan: m.deep_research.plan,
              steps: (m.deep_research.steps || []).map((s) => ({ ...s, done: true })),
              activeIndex: -1,
              liveText: "",
              phase: "done",
            }
          : null,
      })));
      setConversationId(convId);
      setShowHistory(false);
    } catch {
      toast.error("Erreur de chargement");
    }
  };

  const startNew = () => {
    if (convParam) navigate(`/agent/${agentType}`, { replace: true });
    setConversationId(null);
    setMessages([{ role: "assistant", content: agent.greeting }]);
    setShowHistory(false);
    setAttachedFiles([]);
    setShowTemplates(true);
    inputRef.current?.focus();
  };

  // Allowed file types (no HTML or code)
  const ALLOWED_TYPES = [
    'image/jpeg', 'image/png', 'image/gif', 'image/webp',
    'application/pdf',
    'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'text/plain', 'text/csv',
    'audio/mpeg', 'audio/mp3', 'audio/wav', 'audio/m4a', 'audio/ogg', 'audio/webm', 'audio/aac', 'audio/mp4'
  ];

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    // Filter out invalid files
    const validFiles = files.filter(file => {
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name} trop volumineux (max 10 Mo)`);
        return false;
      }
      // Check file type
      const ext = file.name.split('.').pop()?.toLowerCase();
      const blockedExts = ['html', 'htm', 'js', 'jsx', 'ts', 'tsx', 'css', 'py', 'php', 'java', 'cpp', 'c', 'h', 'sh', 'bat'];
      if (blockedExts.includes(ext)) {
        toast.error(`${file.name}: les fichiers de code ne sont pas autorises`);
        return false;
      }
      return true;
    });

    if (!validFiles.length) return;

    setUploading(true);
    const uploadedFiles = [];

    try {
      for (const file of validFiles) {
        const formData = new FormData();
        formData.append("file", file);
        
        // Check if it's an audio file
        const ext = file.name.split('.').pop()?.toLowerCase();
        const isAudioFile = ['mp3', 'wav', 'm4a', 'ogg', 'webm', 'aac'].includes(ext);
        
        if (isAudioFile) {
          // Transcribe audio files
          toast.info(`Transcription de ${file.name}...`);
          const res = await api().post("/transcribe", formData, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          uploadedFiles.push({
            url: res.data.file_url,
            name: file.name,
            size: file.size,
            content: res.data.transcription ? `[Transcription audio] ${res.data.transcription}` : null,
            isAudio: true,
          });
        } else {
          // Regular file upload
          const res = await api().post("/upload", formData, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          uploadedFiles.push({
            url: res.data.file_url,
            name: res.data.file_name,
            size: res.data.file_size,
            content: res.data.extracted_content || null,
          });
        }
      }
      setAttachedFiles(prev => [...prev, ...uploadedFiles]);
      const hasContent = uploadedFiles.some(f => f.content);
      toast.success(`${uploadedFiles.length} fichier(s) attache(s)${hasContent ? ' - contenu extrait' : ''}`);
      if (hasContent) {
        console.log('Files with extracted content:', uploadedFiles.filter(f => f.content).map(f => ({name: f.name, contentLength: f.content?.length})));
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur d'upload");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const removeFile = (index) => {
    setAttachedFiles(prev => prev.filter((_, i) => i !== index));
  };

  // CONSEIL COLLEGIAL : plusieurs experts se prononcent, ODYS'GUIDE arbitre.
  // Declenche uniquement par le detecteur (aucun lancement manuel).
  const runCollegial = async (userMsg) => {
    setMessages((prev) => [...prev, { role: "user", content: userMsg, files: [] }]);
    setSending(true);
    setShowTemplates(false);

    let aiIndex;
    setMessages((prev) => {
      aiIndex = prev.length;
      return [...prev, {
        role: "assistant",
        content: "",
        message_id: null,
        rated: null,
        streaming: true,
        collegial: { experts: [], interventions: [], activeIndex: -1, phase: "routing" },
      }];
    });

    const patch = (updater) => setMessages((prev) => {
      const next = [...prev];
      if (next[aiIndex]) next[aiIndex] = updater({ ...next[aiIndex] });
      return next;
    });

    const token = localStorage.getItem("odys_token");
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    let finalConvId = conversationId;
    let finalMsgId = null;
    let verdict = "";
    let ok = false;

    try {
      await new Promise((resolve, reject) => {
        fetchEventSource(`${backendUrl}/api/chat/collegial-stream`, {
          method: "POST",
          signal: (abortRef.current = new AbortController()).signal,
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
            Accept: "text/event-stream",
          },
          body: JSON.stringify({
            agent_type: agentType,
            message: userMsg,
            conversation_id: conversationId,
          }),
          openWhenHidden: true,
          async onopen(response) {
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
          },
          onmessage(ev) {
            if (!ev.data) return;
            let p;
            try { p = JSON.parse(ev.data); } catch { return; }
            if (ev.event === "meta") {
              finalConvId = p.conversation_id;
              finalMsgId = p.message_id;
            } else if (ev.event === "experts") {
              patch((m) => ({
                ...m,
                collegial: { ...m.collegial, experts: p.experts || [], phase: "experts" },
              }));
            } else if (ev.event === "expert_start") {
              patch((m) => ({ ...m, collegial: { ...m.collegial, activeIndex: p.index } }));
            } else if (ev.event === "expert_end") {
              patch((m) => ({ ...m, collegial: { ...m.collegial, activeIndex: -1 } }));
            } else if (ev.event === "verdict_start") {
              patch((m) => ({ ...m, collegial: { ...m.collegial, phase: "verdict", activeIndex: -1 } }));
            } else if (ev.event === "verdict_token") {
              verdict += p.text || "";
              patch((m) => ({ ...m, content: verdict }));
            } else if (ev.event === "done") {
              ok = true;
              finalConvId = p.conversation_id || finalConvId;
              finalMsgId = p.message_id || finalMsgId;
              if (p.collegial) {
                patch((m) => ({
                  ...m,
                  collegial: { ...m.collegial, ...p.collegial, phase: "done", activeIndex: -1 },
                }));
              }
            } else if (ev.event === "error") {
              reject(new Error(p.error || "Collegial stream error"));
            }
          },
          onclose() { resolve(); },
          onerror(err) { reject(err); throw err; },
        });
      });

      if (!ok) {
        if (!finalMsgId) throw new Error("Conseil interrompu avant demarrage");
        const data = await pollDeepRun(finalMsgId, (updater) => patch((m) => {
          const next = updater(m);
          return next;
        }));
        verdict = data.answer || verdict;
        patch((m) => ({
          ...m,
          collegial: {
            ...m.collegial,
            interventions: data.steps || m.collegial.interventions,
            experts: data.experts || m.collegial.experts,
            phase: "done",
            activeIndex: -1,
          },
        }));
      }

      setConversationId(finalConvId);
      patch((m) => ({
        ...m,
        content: verdict,
        message_id: finalMsgId,
        streaming: false,
        collegial: { ...m.collegial, phase: "done", activeIndex: -1 },
      }));
    } catch (err) {
      console.error("Collegial failed:", err);
      let recovered = false;
      if (finalMsgId) {
        try {
          const data = await pollDeepRun(finalMsgId, patch);
          verdict = data.answer || verdict;
          recovered = true;
        } catch (e2) {
          console.error("Collegial polling failed:", e2);
        }
      }
      if (!recovered) toast.error("Le conseil collegial a echoue. Reessaie dans un instant.");
      patch((m) => ({
        ...m,
        content: verdict || "Desole, le conseil a ete interrompu. Reessaie dans quelques instants.",
        message_id: finalMsgId,
        streaming: false,
        collegial: { ...m.collegial, phase: "done", activeIndex: -1 },
      }));
    } finally {
      setSending(false);
      loadConversations();
    }
  };

  // Polling de secours : l'ingress coupe une reponse SSE a 60s alors qu'une reflexion
  // profonde dure 1 a 3 min. On recupere alors l'etat serveur jusqu'a la fin.
  const pollDeepRun = async (runId, patch) => {
    const token = localStorage.getItem("odys_token");
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/chat/deep/${runId}`;
    for (let i = 0; i < 90; i++) {
      await new Promise((r) => setTimeout(r, 4000));
      const { data } = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
      patch((m) => {
        const next = { ...m, content: data.answer || m.content };
        if (data.experts || m.collegial) {
          next.collegial = {
            ...(m.collegial || {}),
            experts: data.experts || m.collegial?.experts || [],
            interventions: (data.steps || []).length ? data.steps : (m.collegial?.interventions || []),
            phase: data.answer ? "verdict" : "experts",
            activeIndex: -1,
          };
        } else {
          next.deep = {
            ...(m.deep || {}),
            plan: data.plan || m.deep?.plan,
            steps: (data.steps || []).length ? data.steps : (m.deep?.steps || []),
            phase: data.answer ? "answer" : "search",
          };
        }
        return next;
      });
      if (data.status === "done") return data;
      if (data.status === "error") throw new Error(data.error || "Reflexion en erreur");
    }
    throw new Error("Reflexion trop longue");
  };

  const runDeepThinking = async (userMsg) => {
    setMessages((prev) => [...prev, { role: "user", content: userMsg, files: [] }]);
    setSending(true);
    setShowTemplates(false);

    let aiIndex;
    setMessages((prev) => {
      aiIndex = prev.length;
      return [...prev, {
        role: "assistant",
        content: "",
        message_id: null,
        rated: null,
        streaming: true,
        deep: { plan: null, steps: [], activeIndex: -1, liveText: "", phase: "plan" },
      }];
    });

    const patch = (updater) => setMessages((prev) => {
      const next = [...prev];
      if (next[aiIndex]) next[aiIndex] = updater({ ...next[aiIndex] });
      return next;
    });

    const token = localStorage.getItem("odys_token");
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    let finalConvId = conversationId;
    let finalMsgId = null;
    let answer = "";
    let ok = false;

    try {
      await new Promise((resolve, reject) => {
        fetchEventSource(`${backendUrl}/api/chat/deep-stream`, {
          method: "POST",
          signal: (abortRef.current = new AbortController()).signal,
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
            Accept: "text/event-stream",
          },
          body: JSON.stringify({
            agent_type: agentType,
            message: userMsg,
            conversation_id: conversationId,
          }),
          openWhenHidden: true,
          async onopen(response) {
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
          },
          onmessage(ev) {
            if (!ev.data) return;
            let p;
            try { p = JSON.parse(ev.data); } catch { return; }
            if (ev.event === "meta") {
              finalConvId = p.conversation_id;
              finalMsgId = p.message_id;
            } else if (ev.event === "plan") {
              patch((m) => ({
                ...m,
                deep: {
                  ...m.deep,
                  plan: p.plan,
                  steps: (p.plan?.subqueries || []).map((sq) => ({ ...sq, sources: [], done: false, running: false, live: "" })),
                  phase: "search",
                },
              }));
            } else if (ev.event === "step_start") {
              patch((m) => {
                const steps = [...(m.deep.steps || [])];
                if (steps[p.index]) steps[p.index] = { ...steps[p.index], running: true, live: "" };
                return { ...m, deep: { ...m.deep, steps } };
              });
            } else if (ev.event === "step_token") {
              patch((m) => {
                const steps = [...(m.deep.steps || [])];
                if (steps[p.index]) steps[p.index] = { ...steps[p.index], live: ((steps[p.index].live || "") + (p.text || "")).slice(-600) };
                return { ...m, deep: { ...m.deep, steps } };
              });
            } else if (ev.event === "step_end") {
              patch((m) => {
                const steps = [...(m.deep.steps || [])];
                if (steps[p.index]) steps[p.index] = { ...steps[p.index], sources: p.sources || [], done: true, running: false, live: "" };
                return { ...m, deep: { ...m.deep, steps } };
              });
            } else if (ev.event === "answer_token") {
              answer += p.text || "";
              patch((m) => ({ ...m, content: answer, deep: { ...m.deep, phase: "answer", activeIndex: -1 } }));
            } else if (ev.event === "done") {
              ok = true;
              finalConvId = p.conversation_id || finalConvId;
              finalMsgId = p.message_id || finalMsgId;
            } else if (ev.event === "error") {
              reject(new Error(p.error || "Deep stream error"));
            }
          },
          onclose() { resolve(); },
          onerror(err) { reject(err); throw err; },
        });
      });

      if (!ok) {
        // Flux coupe (cap 60s de l'ingress) : la reflexion continue cote serveur.
        if (!finalMsgId) throw new Error("Reflexion interrompue avant demarrage");
        const data = await pollDeepRun(finalMsgId, patch);
        answer = data.answer || answer;
      }

      setConversationId(finalConvId);
      patch((m) => ({
        ...m,
        content: answer,
        message_id: finalMsgId,
        streaming: false,
        deep: { ...m.deep, phase: "done", activeIndex: -1, liveText: "" },
      }));
    } catch (err) {
      console.error("Deep thinking failed:", err);
      let recovered = false;
      if (finalMsgId) {
        try {
          const data = await pollDeepRun(finalMsgId, patch);
          answer = data.answer || answer;
          recovered = true;
        } catch (e2) {
          console.error("Deep polling failed:", e2);
        }
      }
      if (!recovered) {
        toast.error("La reflexion profonde a echoue. Reessaie ou desactive le mode.");
      } else {
        setConversationId(finalConvId);
      }
      patch((m) => ({
        ...m,
        content: answer || "Desole, ma reflexion profonde a ete interrompue. Reessaie dans quelques instants.",
        message_id: finalMsgId,
        streaming: false,
        deep: { ...m.deep, phase: "done", activeIndex: -1, liveText: "" },
      }));
    } finally {
      setSending(false);
      loadConversations();
    }
  };

  const handleSend = async (overrideText) => {
    const effectiveInput = (overrideText ?? input).trim();
    if (!effectiveInput || sending) return;

    // Mode AUTO : le detecteur backend tranche entre reponse directe et analyse renforcee.
    if (attachedFiles.length === 0) {
      if (!overrideText) setInput("");
      let detected = "normal";
      try {
        const token = localStorage.getItem("odys_token");
        const { data } = await axios.post(
          `${API}/chat/mode-check`,
          {
            agent_type: agentType,
            message: effectiveInput,
            conversation_id: conversationId,
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        detected = data?.mode || "normal";
      } catch (e) {
        console.error("mode-check failed, fallback direct:", e);
      }
      if (detected === "collegial") {
        await runCollegial(effectiveInput);
        return;
      }
      if (detected === "deep") {
        await runDeepThinking(effectiveInput);
        return;
      }
      await sendClassic(effectiveInput);
      return;
    }

    await sendClassic(overrideText);
  };

  const sendClassic = async (overrideText) => {
    const effectiveInput = (overrideText ?? input).trim();
    if (!effectiveInput || sending) return;

    const userMsg = effectiveInput;
    const filesData = overrideText ? [] : [...attachedFiles];
    if (!overrideText) {
      setInput("");
      if (inputRef.current) inputRef.current.style.height = "auto";
      setAttachedFiles([]);
    }

    // Build message content with file info
    const filesInfo = filesData.length > 0 
      ? filesData.map(f => f.name).join(", ")
      : null;

    setMessages((prev) => [...prev, {
      role: "user",
      content: userMsg,
      files: filesData.map(f => ({ url: f.url, name: f.name })),
    }]);
    setSending(true);

    try {
      // Include file contents in the message for AI to analyze
      const fileContents = filesData
        .filter(f => f.content)
        .map(f => `[Contenu du fichier ${f.name}]:\n${f.content}`)
        .join("\n\n");
      
      const enrichedMessage = fileContents 
        ? `${userMsg}\n\n${fileContents}`
        : userMsg;

      // Push empty assistant placeholder (will be filled by stream)
      let aiIndex;
      setMessages((prev) => {
        aiIndex = prev.length;
        return [...prev, { role: "assistant", content: "", message_id: null, rated: null, streaming: true }];
      });

      // Smoothing buffer : Claude envoie en chunks irreguliers (2 a 80 chars),
      // on dépile au goutte-à-goutte pour effet machine à écrire fluide.
      const streamQueue = { text: "", timer: null, done: false };
      const drainStep = () => {
        const remaining = streamQueue.text;
        if (!remaining) {
          if (streamQueue.done && streamQueue.timer) {
            clearInterval(streamQueue.timer);
            streamQueue.timer = null;
          }
          return;
        }
        // Adaptive : si grosse queue, on accelere pour rattraper
        const len = remaining.length;
        const chunkSize = len > 400 ? 5 : len > 150 ? 2 : 1;
        const chunk = remaining.slice(0, chunkSize);
        streamQueue.text = remaining.slice(chunkSize);
        setMessages((prev) => {
          const next = [...prev];
          if (next[aiIndex]) {
            next[aiIndex] = { ...next[aiIndex], content: next[aiIndex].content + chunk };
          }
          return next;
        });
      };
      const startDrain = () => {
        if (!streamQueue.timer) {
          streamQueue.timer = setInterval(drainStep, 28); // ~36 ticks/s, plus lent et lisible
        }
      };
      const stopDrain = () => {
        // Flush remaining buffer
        if (streamQueue.text) {
          setMessages((prev) => {
            const next = [...prev];
            if (next[aiIndex]) {
              next[aiIndex] = { ...next[aiIndex], content: next[aiIndex].content + streamQueue.text };
            }
            return next;
          });
          streamQueue.text = "";
        }
        if (streamQueue.timer) {
          clearInterval(streamQueue.timer);
          streamQueue.timer = null;
        }
      };

      const token = localStorage.getItem("odys_token");
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      let streamOk = false;
      let finalConvId = null;
      let finalMsgId = null;
      let webSearchUsed = false;
      let webSources = [];
      let accumulated = "";

      try {
        await new Promise((resolve, reject) => {
          fetchEventSource(`${backendUrl}/api/chat/stream`, {
            method: "POST",
            signal: (abortRef.current = new AbortController()).signal,
            headers: {
              "Content-Type": "application/json",
              "Authorization": `Bearer ${token}`,
              "Accept": "text/event-stream",
            },
            body: JSON.stringify({
              agent_type: agentType,
              message: enrichedMessage,
              conversation_id: conversationId,
              file_url: filesData[0]?.url || null,
              file_name: filesData[0]?.name || null,
              files: filesData.map(f => ({ url: f.url, name: f.name })),
              web_search: false,
            }),
            openWhenHidden: true,
            async onopen(response) {
              if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
              }
            },
            onmessage(ev) {
              if (!ev.data) return;
              let parsed;
              try { parsed = JSON.parse(ev.data); } catch { return; }
              if (ev.event === "meta") {
                finalConvId = parsed.conversation_id;
                finalMsgId = parsed.message_id;
                webSearchUsed = !!parsed.web_search_used;
                webSources = parsed.web_sources || [];
              } else if (ev.event === "token") {
                accumulated += parsed.text || "";
                // Push dans le buffer de smoothing (drain regulier)
                streamQueue.text += parsed.text || "";
                startDrain();
              } else if (ev.event === "done") {
                streamOk = true;
                streamQueue.done = true;
                finalConvId = parsed.conversation_id || finalConvId;
                finalMsgId = parsed.message_id || finalMsgId;
              } else if (ev.event === "error") {
                reject(new Error(parsed.error || "Stream error"));
              }
            },
            onclose() {
              resolve();
            },
            onerror(err) {
              reject(err);
              throw err; // stop retries
            },
          });
        });

        if (!streamOk) throw new Error("Stream ended without 'done' event");

        // Wait for queue to fully drain before finalizing
        await new Promise(r => {
          const check = setInterval(() => {
            if (!streamQueue.text) {
              clearInterval(check);
              stopDrain();
              r();
            }
          }, 20);
        });

        // Finalize message
        setConversationId(finalConvId);
        setMessages((prev) => {
          const next = [...prev];
          if (next[aiIndex]) {
            next[aiIndex] = {
              ...next[aiIndex],
              content: accumulated,
              message_id: finalMsgId,
              streaming: false,
              web_search_used: webSearchUsed,
              web_sources: webSources,
            };
          }
          return next;
        });
        setShowTemplates(false);
      } catch (streamErr) {
        // Fallback to legacy non-stream endpoint
        console.warn("Stream failed, fallback to /chat/send:", streamErr);
        // Remove the streaming placeholder
        setMessages((prev) => prev.filter((_, idx) => idx !== aiIndex));

        const res = await api().post("/chat/send", {
          agent_type: agentType,
          message: enrichedMessage,
          conversation_id: conversationId,
          file_url: filesData[0]?.url || null,
          file_name: filesData[0]?.name || null,
          files: filesData.map(f => ({ url: f.url, name: f.name })),
          web_search: false,
        }, { timeout: 180000 });
        setConversationId(res.data.conversation_id);
        setMessages((prev) => [...prev, {
          role: "assistant",
          content: res.data.message,
          message_id: res.data.message_id,
          rated: null,
          web_search_used: res.data.web_search_used,
          web_sources: res.data.web_sources || [],
        }]);
        setShowTemplates(false);
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 429) {
        setMessages((prev) => [...prev, { role: "assistant", content: detail }]);
      } else {
        toast.error(detail || "Erreur lors de l'envoi");
        setMessages((prev) => [...prev, {
          role: "assistant",
          content: "Desole, une erreur est survenue. Reessaie dans quelques instants.",
        }]);
      }
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };

  const handleFeedback = async (messageIndex, rating) => {
    const msg = messages[messageIndex];
    if (!msg || msg.rated !== null) return; // Already rated
    
    try {
      // Use new feedback API
      await axios.post(`${API}/feedback`, {
        conversation_id: conversationId,
        message_id: msg.message_id,
        feedback_type: rating === 1 ? 'up' : 'down',
        agent_type: agentType
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Update local state to show rating
      setMessages(prev => prev.map((m, i) => 
        i === messageIndex ? { ...m, rated: rating } : m
      ));
      
      toast.success(rating === 1 ? "Merci pour ton retour positif !" : "Merci, on va s'ameliorer !");
    } catch (err) {
      console.error("Feedback error:", err);
    }
  };

  // Handle voice message send
  const handleVoiceSend = async (audioFile, duration) => {
    if (!audioFile || sending) return;
    
    setTranscribing(true);
    
    try {
      // Upload and transcribe the audio
      const formData = new FormData();
      formData.append("file", audioFile);
      
      const res = await api().post("/transcribe", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      
      const transcription = res.data.transcription;
      const audioUrl = res.data.file_url;
      
      if (!transcription || transcription.trim() === "") {
        toast.error("Impossible de transcrire le message vocal");
        setTranscribing(false);
        return;
      }
      
      // Add user message with audio
      setMessages((prev) => [...prev, {
        role: "user",
        content: transcription,
        audio_url: audioUrl,
        is_voice: true,
        duration: duration
      }]);
      
      setSending(true);
      setTranscribing(false);
      setIsRecordingVoice(false);
      
      // Send to AI with voice context
      const voiceMessage = `[Message vocal transcrit] ${transcription}`;
      
      const response = await api().post("/chat/send", {
        agent_type: agentType,
        message: voiceMessage,
        conversation_id: conversationId,
        web_search: false,
      });
      
      const { conversation_id, message: aiMessage, web_sources } = response.data;
      if (!conversationId) setConversationId(conversation_id);
      
      setMessages((prev) => [...prev, {
        role: "assistant",
        content: aiMessage,
        web_sources: web_sources || []
      }]);
      setShowTemplates(false);
      
      toast.success("Message vocal envoyé !");
      
    } catch (err) {
      console.error("Voice send error:", err);
      toast.error(err.response?.data?.detail || "Erreur lors de l'envoi du vocal");
    } finally {
      setSending(false);
      setTranscribing(false);
    }
  };

  const downloadVisitReport = async (messageId) => {
    if (!conversationId || !messageId) return;
    setVisitReportLoading(messageId);
    try {
      const res = await api().post(
        "/chat/visit-report",
        null,
        {
          params: { conversation_id: conversationId, message_id: messageId },
          responseType: "blob",
          timeout: 180000,
        }
      );
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `rapport_visite_${new Date().toISOString().split("T")[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success("Rapport de visite telecharge !");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de la generation du rapport");
    } finally {
      setVisitReportLoading(null);
    }
  };

  const handleExportPDF = async () => {
    if (!conversationId || exporting) return;
    
    setExporting(true);
    try {
      const response = await api().post(
        "/chat/export-pdf",
        { conversation_id: conversationId },
        { responseType: "blob", timeout: 180000 }
      );
      
      // Create download link
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const safeName = (agent?.name || "conversation").toLowerCase().replace(/'/g, "").replace(/\s/g, "_");
      link.download = `${safeName}_${new Date().toISOString().split("T")[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success("PDF telecharge avec succes !");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'export PDF");
    } finally {
      setExporting(false);
    }
  };

  const stopGeneration = () => {
    try { abortRef.current?.abort(); } catch { /* deja termine */ }
    setSending(false);
    setMessages((prev) => prev.map((m) => (m.streaming ? { ...m, streaming: false } : m)));
    toast.info("Generation interrompue");
  };

  const scrollToBottom = () => {
    const el = scrollWrapRef.current?.querySelector("[data-radix-scroll-area-viewport]");
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
  };


  const isImage = (name) => /\.(jpg|jpeg|png|gif|webp)$/i.test(name || "");
  const isAudio = (name) => /\.(mp3|wav|m4a|ogg|webm|aac)$/i.test(name || "");
  const fileIcon = (name) => {
    if (isImage(name)) return ImageIcon;
    if (isAudio(name)) return Volume2;
    return FileText;
  };

  if (!agent) return null;
  const Icon = agent.icon;

  return (
    <div className="h-screen flex flex-col bg-[#F5F5F5] dark:bg-slate-900" data-testid="agent-chat-page">
      {/* Header */}
      <header className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 shrink-0">
        <div className="max-w-4xl mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate("/dashboard")}
              data-testid="back-to-dashboard-btn"
              className="text-[#6B7280] hover:text-[#1F2937] dark:text-slate-400 dark:hover:text-white"
            >
              <ArrowLeft className="w-5 h-5" strokeWidth={1.5} />
            </Button>
            {agent.avatar ? (
              <div className="relative">
                <img
                  src={agent.avatar}
                  alt={agent.name}
                  className="w-10 h-10 rounded-lg object-cover ring-2 ring-offset-1 avatar-bounce"
                  style={{ '--tw-ring-color': agent.color }}
                />
                <div 
                  className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: agent.color }}
                >
                  <div className="w-1.5 h-1.5 bg-white rounded-full animate-online-pulse" />
                </div>
              </div>
            ) : (
              <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${agent.color}10` }}>
                <Icon className="w-5 h-5" style={{ color: agent.color }} strokeWidth={1.5} />
              </div>
            )}
            <div>
              <h2 className="text-sm font-semibold text-[#1F2937] dark:text-white font-heading">{agent.name}</h2>
              <p className="text-xs text-[#6B7280] dark:text-slate-400 hidden sm:block">{agent.desc}</p>
            </div>
          </div>
          {/* Mobile : les actions sont regroupees dans un menu pour laisser respirer l'en-tete */}
          <div className="sm:hidden flex items-center gap-1">
            {canExportPDF && (
              <FavoriteButton
                kind="chat"
                targetId={conversationId}
                agentType={agentType}
                variant="icon"
                summary={(messages.find(m => m.role === "user")?.content || "").slice(0, 200)}
              />
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" data-testid="chat-actions-menu-btn" aria-label="Actions">
                  <MoreHorizontal className="w-5 h-5" strokeWidth={1.8} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <DropdownMenuItem onClick={startNew} data-testid="menu-new-conversation">
                  <Plus className="w-4 h-4 mr-2" /> Nouvelle conversation
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setShowHistory(!showHistory)} data-testid="menu-history">
                  <History className="w-4 h-4 mr-2" /> Historique
                </DropdownMenuItem>
                {canExportPDF && (
                  <DropdownMenuItem onClick={handleExportPDF} disabled={exporting} data-testid="menu-export-pdf">
                    <FileDown className="w-4 h-4 mr-2" /> {exporting ? "Export en cours..." : "Exporter en PDF"}
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          <div className="hidden sm:flex items-center gap-2">
            {/* Favori : disponible des qu'on a une conversation et au moins une reponse */}
            {canExportPDF && (
              <FavoriteButton
                kind="chat"
                targetId={conversationId}
                agentType={agentType}
                variant="icon"
                summary={(messages.find(m => m.role === "user")?.content || "").slice(0, 200)}
              />
            )}
            {/* Export PDF button - available for all agents */}
            {canExportPDF && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleExportPDF}
                disabled={exporting}
                data-testid="export-pdf-btn"
                className="text-[#FF8C42] border-[#FF8C42]/30 hover:bg-[#FF8C42]/5"
              >
                {exporting ? (
                  <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                ) : (
                  <FileDown className="w-3 h-3 mr-1" />
                )}
                Exporter PDF
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowHistory(!showHistory)}
              data-testid="toggle-history-btn"
              className="text-[#6B7280] dark:text-slate-400 text-xs"
            >
              Historique
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={startNew}
              data-testid="new-conversation-btn"
              className="text-[#16233B] dark:text-[#FF8C42] border-[#16233B]/20 dark:border-[#FF8C42]/30 hover:bg-[#16233B]/5 dark:hover:bg-[#FF8C42]/10"
            >
              <Plus className="w-3 h-3 mr-1" />
              Nouveau
            </Button>
          </div>
        </div>
      </header>

      <div className="flex-1 flex overflow-hidden">
        {/* Sidebar */}
        {showHistory && (
          <aside className="w-64 bg-white dark:bg-slate-800 border-r border-slate-200 dark:border-slate-700 overflow-y-auto shrink-0" data-testid="conversation-history-sidebar">
            <div className="p-3 space-y-1">
              <input
                type="text"
                value={histQuery}
                onChange={(e) => setHistQuery(e.target.value)}
                placeholder="Rechercher..."
                data-testid="history-search-input"
                spellCheck={false}
                className="w-full mb-2 px-2.5 py-2 text-xs rounded-md border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-[#1F2937] dark:text-white focus:outline-none focus:border-[#16233B]"
              />
              {conversations.length === 0 ? (
                <p className="text-xs text-[#6B7280] dark:text-slate-400 p-3">Aucune conversation</p>
              ) : (
                conversations
                  .filter((c) => !histQuery.trim() ||
                    (c.title || "").toLowerCase().includes(histQuery.trim().toLowerCase()))
                  .map((conv) => (
                  <button
                    key={conv.conversation_id}
                    onClick={() => loadConversation(conv.conversation_id)}
                    className={`w-full text-left p-2.5 rounded-lg text-xs transition-colors ${
                      conversationId === conv.conversation_id
                        ? "bg-[#16233B]/5 dark:bg-[#FF8C42]/10 text-[#16233B] dark:text-[#FF8C42]"
                        : "hover:bg-slate-50 dark:hover:bg-slate-700 text-[#6B7280] dark:text-slate-400"
                    }`}
                  >
                    <p className="font-medium truncate">{conv.title || "Conversation"}</p>
                    <p className="text-[10px] mt-0.5 opacity-60">
                      {new Date(conv.updated_at).toLocaleDateString("fr-FR")}
                    </p>
                  </button>
                ))
              )}
            </div>
          </aside>
        )}

        {/* Chat Area */}
        <div className="flex-1 flex flex-col">
          <ScrollArea
            className="flex-1 px-4 py-6 relative"
            ref={scrollWrapRef}
          >
            <div className="max-w-3xl mx-auto space-y-4 chat-reading-col">
              {messages.map((msg, i) => (
                <div
                  key={i}
                  className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
                  data-testid={`chat-message-${i}`}
                >
                  {msg.role === "assistant" && (
                    agent.avatar ? (
                      <div className="relative shrink-0 mr-2 mt-1 avatar-entrance">
                        <img
                          src={agent.avatar}
                          alt={agent.name}
                          className="w-9 h-9 rounded-lg object-cover ring-1 ring-offset-1 avatar-wave"
                          style={{ '--tw-ring-color': `${agent.color}40` }}
                        />
                      </div>
                    ) : (
                      <div
                        className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 mr-2 mt-1"
                        style={{ backgroundColor: `${agent.color}15` }}
                      >
                        <Icon className="w-4 h-4" style={{ color: agent.color }} strokeWidth={1.5} />
                      </div>
                    )
                  )}
                  <div
                    className={`max-w-[80%] rounded-xl px-4 py-3 text-sm leading-relaxed ${
                      msg.role === "user"
                        ? "bg-[#16233B] text-white rounded-br-sm"
                        : "bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-[#1F2937] dark:text-slate-200 rounded-bl-sm shadow-sm"
                    }`}
                  >
                    {/* Voice message player */}
                    {msg.is_voice && msg.audio_url && (
                      <div className={`flex items-center gap-2 mb-2 p-2 rounded-lg ${
                        msg.role === "user" ? "bg-white/10" : "bg-slate-50 dark:bg-slate-700"
                      }`}>
                        <Volume2 className="w-4 h-4 shrink-0" />
                        <audio 
                          controls 
                          src={`${process.env.REACT_APP_BACKEND_URL}${msg.audio_url}`}
                          className="h-8 flex-1"
                          style={{ maxWidth: '200px' }}
                        />
                        {msg.duration && (
                          <span className={`text-xs ${msg.role === "user" ? "text-white/60" : "text-slate-400"}`}>
                            {Math.floor(msg.duration / 60)}:{(msg.duration % 60).toString().padStart(2, '0')}
                          </span>
                        )}
                      </div>
                    )}
                    {/* Multiple file attachments */}
                    {msg.files && msg.files.length > 0 && (
                      <div className="space-y-1 mb-2">
                        {msg.files.map((file, idx) => (
                          <div key={idx} className={`flex items-center gap-2 p-2 rounded-lg text-xs ${
                            msg.role === "user" ? "bg-white/10" : "bg-slate-50 dark:bg-slate-700"
                          }`}>
                            {isAudio(file.name) ? (
                              <>
                                <Volume2 className="w-3.5 h-3.5 shrink-0" />
                                <audio 
                                  controls 
                                  src={`${process.env.REACT_APP_BACKEND_URL}${file.url}`}
                                  className="h-8 flex-1"
                                  style={{ maxWidth: '180px' }}
                                />
                              </>
                            ) : isImage(file.name) ? (
                              <a
                                href={`${process.env.REACT_APP_BACKEND_URL}${file.url}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-2 min-w-0"
                                data-testid={`attachment-image-${idx}`}
                              >
                                <img
                                  src={`${process.env.REACT_APP_BACKEND_URL}${file.url}`}
                                  alt={file.name}
                                  className="w-12 h-12 rounded-md object-cover shrink-0 border border-black/10"
                                />
                                <span className={`truncate hover:underline ${msg.role === "user" ? "text-white/80" : "text-[#16233B]"}`}>
                                  {file.name}
                                </span>
                              </a>
                            ) : (
                              <>
                                <FileText className="w-3.5 h-3.5 shrink-0" />
                                <a
                                  href={`${process.env.REACT_APP_BACKEND_URL}${file.url}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className={`truncate hover:underline ${msg.role === "user" ? "text-white/80" : "text-[#16233B]"}`}
                                >
                                  {file.name}
                                </a>
                              </>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                    {/* Legacy single file attachment */}
                    {!msg.files && msg.file_url && msg.file_name && (
                      <div className={`flex items-center gap-2 mb-2 p-2 rounded-lg text-xs ${
                        msg.role === "user" ? "bg-white/10" : "bg-slate-50 dark:bg-slate-700"
                      }`}>
                        {isAudio(msg.file_name) ? (
                          <>
                            <Volume2 className="w-3.5 h-3.5 shrink-0" />
                            <audio 
                              controls 
                              src={`${process.env.REACT_APP_BACKEND_URL}${msg.file_url}`}
                              className="h-8 flex-1"
                              style={{ maxWidth: '180px' }}
                            />
                          </>
                        ) : isImage(msg.file_name) ? (
                          <>
                            <ImageIcon className="w-3.5 h-3.5 shrink-0" />
                            <a
                              href={`${process.env.REACT_APP_BACKEND_URL}${msg.file_url}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={`truncate hover:underline ${msg.role === "user" ? "text-white/80" : "text-[#16233B]"}`}
                            >
                              {msg.file_name}
                            </a>
                          </>
                        ) : (
                          <>
                            <FileText className="w-3.5 h-3.5 shrink-0" />
                            <a
                              href={`${process.env.REACT_APP_BACKEND_URL}${msg.file_url}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={`truncate hover:underline ${msg.role === "user" ? "text-white/80" : "text-[#16233B]"}`}
                            >
                              {msg.file_name}
                            </a>
                          </>
                        )}
                      </div>
                    )}
                    {msg.role === "assistant" ? (
                      <>
                        {msg.deep && <DeepThinkingPanel deep={msg.deep} agentName={agent?.name || "L'expert"} index={i} />}
                        {msg.collegial && <CollegialPanel collegial={msg.collegial} index={i} />}
                        {msg.content && msg.content.trim() && (
                          <div className="mb-2 flex items-center gap-2">
                            <AiGeneratedBadge label={`Reponse IA - ${agent?.name || "Odys'IA"}`} size="xs" />
                          </div>
                        )}
                        <div className="chat-message-body" data-streaming={msg.streaming ? "true" : "false"}>
                          <RichMessage
                            text={stripFollowup(msg.content || "")}
                            streaming={msg.streaming}
                            index={i}
                          />
                        </div>
                      </>
                    ) : (
                      <p className="whitespace-pre-wrap">{msg.content}</p>
                    )}
                    {/* Follow-up CTA : si l'agent a inclus des questions structurees */}
                    {msg.role === "assistant" && !msg.streaming && (() => {
                      const questions = parseFollowup(msg.content || "");
                      if (!questions.length) return null;
                      return (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setFollowUpState({ open: true, questions, msgIndex: i });
                          }}
                          data-testid={`followup-cta-${i}`}
                          className="mt-3 w-full flex items-center justify-between gap-3 px-4 py-3 rounded-lg group transition-all text-left"
                          style={{
                            background: "linear-gradient(180deg, #fff, var(--gold-bg))",
                            border: "1px solid var(--gold-soft)",
                            borderLeft: "4px solid var(--gold)",
                          }}
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            <span
                              className="w-8 h-8 rounded-md flex items-center justify-center shrink-0"
                              style={{ background: "var(--gold)" }}
                            >
                              <MessageSquareText className="w-4 h-4 text-white" strokeWidth={1.8} />
                            </span>
                            <span className="min-w-0">
                              <span className="block text-[0.62rem] uppercase font-semibold" style={{ color: "var(--gold-dark)", letterSpacing: "0.22em" }}>
                                {questions.length} question{questions.length > 1 ? "s" : ""} pour affiner
                              </span>
                              <span className="block text-sm font-semibold" style={{ color: "var(--ink)", fontFamily: "var(--serif)" }}>
                                Repondre a {agent?.name || "l'expert"}
                              </span>
                            </span>
                          </div>
                          <ChevronRight className="w-4 h-4 shrink-0 group-hover:translate-x-1 transition-transform" style={{ color: "var(--gold)" }} />
                        </button>
                      );
                    })()}
                    {/* Web search indicator with sources */}
                    {msg.web_search_used && (
                      <WebSourcesBadge sources={msg.web_sources || []} index={i} />
                    )}
                    {/* Visit report PDF button - shown when previous user message has images */}
                    {msg.role === "assistant" && !msg.streaming && (() => {
                      const prev = messages[i - 1];
                      if (!prev || prev.role !== "user") return null;
                      const prevFiles = prev.files || [];
                      const hasImages = prevFiles.some(f =>
                        /\.(jpe?g|png|webp|gif)$/i.test(f.name || "")
                      ) || (prev.file_name && /\.(jpe?g|png|webp|gif)$/i.test(prev.file_name));
                      if (!hasImages) return null;
                      return (
                        <button
                          onClick={() => downloadVisitReport(msg.message_id)}
                          disabled={visitReportLoading === msg.message_id}
                          data-testid={`visit-report-btn-${i}`}
                          className="mt-3 inline-flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold border transition-all"
                          style={{
                            background: "var(--gold-bg, #F9F3E6)",
                            borderColor: "var(--gold, #B5894A)",
                            color: "var(--ink, #0B1E3A)",
                          }}
                        >
                          {visitReportLoading === msg.message_id ? (
                            <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Generation...</>
                          ) : (
                            <><FileText className="w-3.5 h-3.5" style={{color: "var(--gold, #B5894A)"}} /> Generer rapport de visite (PDF)</>
                          )}
                        </button>
                      );
                    })()}
                    {/* Inline feedback for assistant messages */}
                    {msg.role === "assistant" && i > 0 && !msg.streaming && (msg.content || "").trim() && (
                      <div className="flex items-center gap-2 mt-2 pt-2 border-t border-slate-100">
                        {msg.rated === null ? (
                          <>
                            <span className="text-[10px] text-[#9CA3AF]">Utile ?</span>
                            <button 
                              onClick={(e) => { e.stopPropagation(); handleFeedback(i, 1); }}
                              data-testid={`feedback-up-${i}`}
                              className="p-1 rounded hover:bg-emerald-50 text-slate-300 hover:text-emerald-500 transition-colors"
                            >
                              <ThumbsUp className="w-3 h-3" />
                            </button>
                            <button 
                              onClick={(e) => { e.stopPropagation(); handleFeedback(i, -1); }}
                              data-testid={`feedback-down-${i}`}
                              className="p-1 rounded hover:bg-red-50 text-slate-300 hover:text-red-400 transition-colors"
                            >
                              <ThumbsDown className="w-3 h-3" />
                            </button>
                          </>
                        ) : (
                          <span className={`text-[10px] flex items-center gap-1 ${msg.rated === 1 ? 'text-emerald-500' : 'text-red-400'}`}>
                            {msg.rated === 1 ? <ThumbsUp className="w-3 h-3" /> : <ThumbsDown className="w-3 h-3" />}
                            Merci !
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}

              {sending && !messages.some((m) => m.streaming) && (
                <div className="flex justify-start" data-testid="typing-indicator">
                  {agent.avatar ? (
                    <div className="relative shrink-0 mr-2">
                      <img
                        src={agent.avatar}
                        alt={agent.name}
                        className="w-9 h-9 rounded-lg object-cover ring-1 ring-offset-1 avatar-thinking"
                        style={{ '--tw-ring-color': `${agent.color}40` }}
                      />
                      <div 
                        className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full flex items-center justify-center"
                        style={{ backgroundColor: agent.color }}
                      >
                        <div className="w-1.5 h-1.5 bg-white rounded-full animate-ping" />
                      </div>
                    </div>
                  ) : (
                    <div
                      className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 mr-2"
                      style={{ backgroundColor: `${agent.color}15` }}
                    >
                      <Icon className="w-4 h-4" style={{ color: agent.color }} strokeWidth={1.5} />
                    </div>
                  )}
                  <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl rounded-bl-sm px-4 py-3 shadow-sm">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-slate-400">Reflexion en cours</span>
                      <div className="flex items-center gap-1">
                        <div className="w-1.5 h-1.5 rounded-full animate-bounce [animation-delay:0ms]" style={{ backgroundColor: agent.color }} />
                        <div className="w-1.5 h-1.5 rounded-full animate-bounce [animation-delay:150ms]" style={{ backgroundColor: agent.color }} />
                        <div className="w-1.5 h-1.5 rounded-full animate-bounce [animation-delay:300ms]" style={{ backgroundColor: agent.color }} />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>
          </ScrollArea>
          {showScrollDown && (
            <button
              type="button"
              onClick={scrollToBottom}
              data-testid="scroll-to-bottom-btn"
              aria-label="Revenir en bas de la conversation"
              className="chat-scroll-down"
            >
              <ChevronDown className="w-5 h-5" strokeWidth={2} />
            </button>
          )}

          {/* Input Area */}
          <div className="bg-white dark:bg-slate-800 border-t border-slate-200 dark:border-slate-700 p-4 shrink-0 chat-composer-wrap">
            <div className="max-w-3xl mx-auto chat-reading-col">
              {/* Templates de questions - shown only when no user messages yet */}
              {showTemplates && messages.filter(m => m.role === "user").length === 0 && agent?.templates && (
                <div className="mb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Lightbulb className="w-4 h-4 text-[#F5B731]" />
                    <span className="text-xs font-medium text-slate-500 dark:text-slate-400">Questions frequentes</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {agent.templates.map((template, idx) => (
                      <button
                        key={idx}
                        onClick={() => handleTemplateClick(template)}
                        className="text-xs px-3 py-2 rounded-full bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-[#16233B]/10 hover:text-[#16233B] dark:hover:bg-[#16233B]/30 dark:hover:text-white transition-colors border border-transparent hover:border-[#16233B]/20"
                        data-testid={`template-btn-${idx}`}
                      >
                        {template}
                      </button>
                    ))}
                  </div>

                </div>
              )}
              
              {/* Attached files preview - multiple files */}
              {attachedFiles.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-2">
                  {attachedFiles.map((file, idx) => (
                    <div key={idx} className="flex items-center gap-2 p-2 bg-[#16233B]/5 dark:bg-[#16233B]/20 rounded-lg">
                      {isImage(file.name) ? (
                        <ImageIcon className="w-4 h-4 text-[#16233B] dark:text-[#FF8C42]" />
                      ) : (
                        <FileText className="w-4 h-4 text-[#16233B] dark:text-[#FF8C42]" />
                      )}
                      <span className="text-xs text-[#1F2937] dark:text-slate-200 truncate max-w-[150px]">{file.name}</span>
                      <button
                        onClick={() => removeFile(idx)}
                        className="text-[#6B7280] hover:text-red-500"
                        data-testid={`remove-file-btn-${idx}`}
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <form onSubmit={(e) => { e.preventDefault(); handleSend(); }} className="flex items-end gap-2">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileSelect}
                  className="hidden"
                  multiple
                  accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,audio/*,.mp3,.wav,.m4a,.ogg,.webm,.aac"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  data-testid="attach-file-btn"
                  className="text-[#6B7280] hover:text-[#16233B] hover:bg-[#16233B]/5 shrink-0"
                >
                  {uploading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Paperclip className="w-4 h-4" strokeWidth={1.5} />
                  )}
                </Button>
                {/* Voice Recorder Button */}
                <VoiceRecorder 
                  onSend={handleVoiceSend}
                  onCancel={() => setIsRecordingVoice(false)}
                  disabled={sending || transcribing}
                />
                
                <textarea
                  ref={inputRef}
                  data-testid="chat-input"
                  value={input}
                  rows={1}
                  spellCheck={false}
                  autoCorrect="off"
                  onChange={(e) => {
                    setInput(e.target.value);
                    e.target.style.height = "auto";
                    e.target.style.height = `${Math.min(e.target.scrollHeight, 150)}px`;
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      handleSend();
                    }
                  }}
                  placeholder="Ecris ton message..."
                  className="chat-composer-input"
                  disabled={sending || transcribing}
                />
                {sending ? (
                  <Button
                    type="button"
                    onClick={stopGeneration}
                    data-testid="chat-stop-btn"
                    aria-label="Arreter la generation"
                    className="h-11 w-11 p-0 bg-white hover:bg-slate-50 text-[#16233B] border border-[#16233B]/30 rounded-lg shrink-0 active:scale-95 transition-transform"
                  >
                    <Square className="w-3.5 h-3.5 fill-current" strokeWidth={1.5} />
                  </Button>
                ) : (
                  <Button
                    type="submit"
                    disabled={!input.trim() || transcribing}
                    data-testid="chat-send-btn"
                    className="h-11 w-11 p-0 bg-[#16233B] hover:bg-[#162B47] text-white rounded-lg shrink-0 active:scale-95 transition-transform"
                  >
                    {transcribing ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4" strokeWidth={1.5} />
                    )}
                  </Button>
                )}
              </form>
              
              {/* Disclaimer */}
              <p className="text-[10px] text-slate-400 text-center mt-2 px-2 chat-legal">
                {transcribing && (
                  <span className="text-blue-600 font-medium mr-2">
                    Transcription en cours...
                  </span>
                )}
                <button
                  type="button"
                  onClick={() => setShowDisclaimer((v) => !v)}
                  data-testid="chat-legal-toggle"
                  className="chat-legal-toggle"
                >
                  <span className="font-medium" style={{ color: "#8C6635" }} data-testid="chat-mode-hint">
                    L'IA peut se tromper - informations a titre indicatif
                  </span>
                  <ChevronDown className={`w-3 h-3 inline-block ml-1 transition-transform ${showDisclaimer ? "rotate-180" : ""}`} />
                </button>
                {showDisclaimer && (
                  <span className="block mt-1" data-testid="chat-legal-detail">
                    Sur les questions pointues, l'expert verifie les donnees a jour avant de repondre.
                    Informations a titre indicatif uniquement.{" "}
                    <a href="/cgu" target="_blank" className="underline hover:text-slate-600">Verifiez toujours aupres d'un professionnel.</a>
                  </span>
                )}
              </p>
            </div>
          </div>
        </div>

        <DossierPanel conversationId={conversationId} refreshKey={messages.length} />
      </div>

      {/* Bottom-sheet pour repondre aux questions structurees d'un agent */}
      <FollowUpSheet
        open={followUpState.open}
        onOpenChange={(open) => setFollowUpState((prev) => ({ ...prev, open }))}
        questions={followUpState.questions}
        agentName={agent?.name}
        onSubmit={(text) => {
          handleSend(text);
        }}
      />
    </div>
  );
}
